import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import PageHeader from '@/components/common/PageHeader.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import Switch from '@/components/ui/switch.jsx';
import {
  getFojaOperationalPolicy,
  updateFojaOperationalPolicy,
} from '@/services/fojas.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';

const MODOS = [
  {
    value: 'BLOQUEAR',
    label: 'Bloquear',
    help: 'Si la foja supera un límite, no se puede emitir.',
  },
  {
    value: 'ADVERTIR_Y_PERMITIR',
    label: 'Advertir y permitir',
    help: 'Muestra advertencia operativa, pero deja continuar.',
  },
  {
    value: 'PERMITIR_CON_OVERRIDE',
    label: 'Permitir con override',
    help: 'Solo deja emitir si se autoriza el override según la política.',
  },
];

const EMPTY_FORM = {
  modo: 'ADVERTIR_Y_PERMITIR',
  foja_max_guias: '',
  foja_max_bultos: '',
  foja_max_kilos: '',
  foja_max_m3: '',
  foja_max_valor_declarado: '',
  foja_override_requiere_permiso: true,
  foja_override_requiere_motivo: true,
};

const resolveEmpresaId = (auth) => (
  auth?.user?.empresaId
  || auth?.user?.empresa_id
  || auth?.user?.empresa?.id
  || auth?.tenant?.empresaId
  || auth?.tenant?.empresa_id
  || auth?.tenant?.empresa?.id
  || auth?.tenant?.id
  || auth?.sucursalActiva?.empresa_id
  || auth?.sucursalActiva?.empresaId
  || auth?.sucursal?.empresa_id
  || auth?.sucursal?.empresaId
  || null
);

const toInputValue = (value) => (value == null ? '' : String(value));
const toNullableNumber = (value) => {
  if (value == null || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : null;
};

export default function FojasPoliticasPage() {
  const auth = useAuth();
  const empresaId = resolveEmpresaId(auth);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    getFojaOperationalPolicy(empresaId)
      .then((data) => {
        if (!active) return;
        setForm({
          modo: data?.modo || EMPTY_FORM.modo,
          foja_max_guias: toInputValue(data?.foja_max_guias),
          foja_max_bultos: toInputValue(data?.foja_max_bultos),
          foja_max_kilos: toInputValue(data?.foja_max_kilos),
          foja_max_m3: toInputValue(data?.foja_max_m3),
          foja_max_valor_declarado: toInputValue(data?.foja_max_valor_declarado),
          foja_override_requiere_permiso: data?.foja_override_requiere_permiso !== false,
          foja_override_requiere_motivo: data?.foja_override_requiere_motivo !== false,
        });
      })
      .catch((err) => {
        if (!active) return;
        setError(err?.message || 'No se pudo cargar la política operativa de fojas.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [empresaId]);

  const selectedMode = useMemo(
    () => MODOS.find((item) => item.value === form.modo) || MODOS[1],
    [form.modo],
  );

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const save = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await updateFojaOperationalPolicy({
        empresa_id: empresaId,
        modo: form.modo,
        foja_max_guias: toNullableNumber(form.foja_max_guias),
        foja_max_bultos: toNullableNumber(form.foja_max_bultos),
        foja_max_kilos: toNullableNumber(form.foja_max_kilos),
        foja_max_m3: toNullableNumber(form.foja_max_m3),
        foja_max_valor_declarado: toNullableNumber(form.foja_max_valor_declarado),
        foja_override_requiere_permiso: Boolean(form.foja_override_requiere_permiso),
        foja_override_requiere_motivo: Boolean(form.foja_override_requiere_motivo),
      });
      setMessage('Política operativa guardada.');
    } catch (err) {
      setError(err?.message || 'No se pudo guardar la política operativa de fojas.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1180px] px-4 py-4">
      <PageHeader
        title="Políticas operativas de fojas"
        description="Definí si la emisión bloquea, advierte o exige override cuando una foja supera límites de carga."
        breadcrumbs={[
          { label: 'Gerencial', href: '/gerencial/dashboard' },
          { label: 'Configuración' },
          { label: 'Fojas' },
        ]}
        actions={<Link to="/fojas/emision" className="rounded-xl border border-[rgb(var(--border))] px-3 py-2 text-sm font-semibold">Ir a emisión</Link>}
      />

      <section className="rounded-2xl border border-[rgba(var(--border),0.65)] bg-[rgb(var(--panel))] p-4 shadow-[0_18px_36px_rgba(var(--shadow),0.08)]">
        <div className="flex items-start gap-3 rounded-2xl border border-[rgba(var(--border),0.35)] bg-[rgba(var(--subpanel),0.72)] px-4 py-3">
          <ShieldAlert className="mt-0.5 h-5 w-5 text-[rgb(var(--brand))]" />
          <div>
            <h2 className="text-base font-semibold text-[rgb(var(--fg))]">Emisión con control por tenant</h2>
            <p className="mt-1 text-sm text-[rgba(var(--fg),0.72)]">
              Los límites vacíos se consideran sin tope. Las reglas duras siguen bloqueando aunque exista override.
            </p>
          </div>
        </div>

        {loading ? <p className="mt-4 text-sm text-[rgba(var(--fg),0.72)]">Cargando política…</p> : null}
        {error ? <div className="mt-4 rounded-2xl border border-red-400/40 bg-red-500/10 px-3 py-2 text-sm text-red-100">{error}</div> : null}
        {message ? <div className="mt-4 rounded-2xl border border-emerald-400/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-50">{message}</div> : null}

        <div className="mt-4 grid gap-4 lg:grid-cols-[340px_minmax(0,1fr)]">
          <article className="rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--subpanel),0.62)] p-4">
            <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[rgba(var(--fg),0.55)]">Modo de control</label>
            <select
              className="mt-2 h-10 w-full rounded-xl border border-[rgba(var(--border),0.5)] bg-[rgb(var(--panel))] px-3 text-sm"
              value={form.modo}
              onChange={(event) => updateField('modo', event.target.value)}
              disabled={loading || saving}
            >
              {MODOS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
            <div className="mt-3 rounded-2xl border border-[rgba(var(--border),0.35)] bg-[rgba(var(--panel),0.8)] p-3 text-sm text-[rgba(var(--fg),0.74)]">
              {selectedMode.help}
            </div>

            <div className="mt-4 space-y-3">
              <div className="flex items-start justify-between gap-3 rounded-2xl border border-[rgba(var(--border),0.35)] bg-[rgba(var(--panel),0.78)] px-3 py-3">
                <div>
                  <div className="text-sm font-semibold">Override requiere permiso</div>
                  <div className="text-xs text-[rgba(var(--fg),0.65)]">Exige permiso explícito para pasar un límite.</div>
                </div>
                <Switch
                  checked={Boolean(form.foja_override_requiere_permiso)}
                  onCheckedChange={(checked) => updateField('foja_override_requiere_permiso', checked)}
                  disabled={loading || saving}
                />
              </div>
              <div className="flex items-start justify-between gap-3 rounded-2xl border border-[rgba(var(--border),0.35)] bg-[rgba(var(--panel),0.78)] px-3 py-3">
                <div>
                  <div className="text-sm font-semibold">Override requiere motivo</div>
                  <div className="text-xs text-[rgba(var(--fg),0.65)]">Obliga a registrar por qué se autorizó la excepción.</div>
                </div>
                <Switch
                  checked={Boolean(form.foja_override_requiere_motivo)}
                  onCheckedChange={(checked) => updateField('foja_override_requiere_motivo', checked)}
                  disabled={loading || saving}
                />
              </div>
            </div>
          </article>

          <article className="rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--panel),0.92)] p-4">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {[
                ['foja_max_guias', 'Máx. guías', 'Cantidad máxima de guías en la foja.'],
                ['foja_max_bultos', 'Máx. bultos', 'Tope operativo de bultos.'],
                ['foja_max_kilos', 'Máx. kilos', 'Peso total permitido.'],
                ['foja_max_m3', 'Máx. m3', 'Volumen total permitido.'],
                ['foja_max_valor_declarado', 'Máx. valor declarado', 'Tope económico declarado por foja.'],
              ].map(([field, label, help]) => (
                <label key={field} className="rounded-2xl border border-[rgba(var(--border),0.38)] bg-[rgba(var(--subpanel),0.58)] p-3">
                  <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-[rgba(var(--fg),0.55)]">{label}</span>
                  <input
                    type="number"
                    min="0"
                    step="0.001"
                    value={form[field]}
                    onChange={(event) => updateField(field, event.target.value)}
                    placeholder="Sin límite"
                    disabled={loading || saving}
                    className="mt-2 h-10 w-full rounded-xl border border-[rgba(var(--border),0.5)] bg-[rgb(var(--panel))] px-3 text-sm"
                  />
                  <span className="mt-2 block text-xs text-[rgba(var(--fg),0.64)]">{help}</span>
                </label>
              ))}
            </div>

            <div className="mt-4 rounded-2xl border border-dashed border-[rgba(var(--border),0.45)] px-3 py-3 text-sm text-[rgba(var(--fg),0.72)]">
              <strong>Reglas duras:</strong> nunca admiten override guías ya incluidas en otra foja activa, mezcla de línea o tramo, origen distinto, destino operativo distinto, guía anulada, entregada o no elegible.
            </div>
          </article>
        </div>

        <StickyActions
          onCancel={() => window.history.back()}
          onSubmit={save}
          isSubmitting={saving}
          primaryLabel="Guardar política"
          secondaryLabel="Volver"
        />
      </section>
    </div>
  );
}
